import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Eye } from "lucide-react";
import { imageUrl, trackEvent } from "../../lib/api";
import { quoteHref } from "../../data/homeContent";
import { doorOnderwerp, labelsOf, materialLabel, srcSetOf } from "../../lib/catalog";

export const DoorImage = ({ door, path = door.image, sizes, className = "", eager = false }) => (
  <img src={imageUrl(path)} srcSet={srcSetOf(path)} sizes={sizes} alt={door.image_alt} loading={eager ? "eager" : "lazy"} decoding="async"
    className={`h-full w-full object-cover ${className}`} />
);

export const ColorDots = ({ ids, opts, max = 6 }) => (
  <ul className="flex items-center gap-1.5" aria-label="Beschikbare kleuren">
    {ids.slice(0, max).map((id) => (
      <li key={id} title={opts[id]?.label} className={`h-4 w-4 rounded-full ring-1 ring-inset ring-black/15 ${opts[id]?.swatch ? "" : "bg-[conic-gradient(#e11d48,#f59e0b,#16a34a,#2563eb,#9333ea,#e11d48)]"}`}
        style={opts[id]?.swatch ? { backgroundColor: opts[id].swatch } : undefined}><span className="sr-only">{opts[id]?.label}</span></li>
    ))}
    {ids.length > max && <li className="text-[12px] text-slate-500">+{ids.length - max}</li>}
  </ul>
);

export const DoorCard = ({ door, opts, index, onOpen }) => {
  const { pathname } = useLocation();
  const open = () => { trackEvent("door_view", { door: door.id }); onOpen(); };
  const meta = [...labelsOf(door.styles, opts), door.has_glass ? "Met glas" : "Zonder glas"].join(" · ");
  return (
    <li className="reveal card group flex flex-col overflow-hidden" style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }} data-testid={`door-card-${door.id}`}>
      <button type="button" onClick={open} className="relative block aspect-[4/5] overflow-hidden bg-gradient-to-b from-slate-100 to-slate-200/70" aria-label={`Bekijk ${door.title}`}>
        <DoorImage door={door} sizes="(min-width: 1280px) 300px, (min-width: 640px) 45vw, 100vw" className="transition-transform duration-500 group-hover:scale-[1.03]" />
        {door.image_kind === "inspiratie" && <span className="badge-sfeer">Inspiratiebeeld</span>}
        <span className="absolute right-3 top-3 rounded-full bg-ink/85 px-2.5 py-1 text-[11.5px] font-semibold text-white backdrop-blur">{materialLabel(door.material)}</span>
      </button>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-gold">{labelsOf(door.types, opts).join(" · ") || "Deur"}</p>
        <h3 className="mt-1.5 text-[17px] font-semibold leading-snug text-ink">{door.title}</h3>
        <p className="mt-1 text-[13.5px] text-slate-500">{meta}</p>
        {door.colors.length > 0 && <div className="mt-3"><ColorDots ids={door.colors} opts={opts} /></div>}
        <div className="mt-auto grid gap-2 pt-5">
          <Link to={quoteHref(doorOnderwerp(door), { deur: door.id })} state={{ from: pathname }} data-testid={`door-quote-${door.id}`}
            onClick={() => trackEvent("cta_quote_click", { location: "door-card", door: door.id })} className="btn-primary !min-h-[44px] !px-3 text-[13.5px]">
            Ontvang een prijsindicatie <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <button type="button" onClick={open} className="btn-secondary !min-h-[44px] !px-3 text-[13.5px]" data-testid={`door-view-${door.id}`}>
            <Eye className="h-4 w-4" aria-hidden="true" /> Bekijk deze deur
          </button>
        </div>
      </div>
    </li>
  );
};
